<script setup lang="ts">
import RcDisplaySerialNumber from '@/components/rc/common/RcDisplaySerialNumber.vue'
import type { IModel } from '@/types/rc/miners'
import { Icon } from '@iconify/vue'
import { onClickOutside } from '@vueuse/core'
import { ElMessage } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { vMaska } from 'maska/vue'
import { computed, nextTick, ref } from 'vue'

// If options is set, input will be an ElSelect
const props = defineProps<{
  val?: string | IModel
  required?: boolean
  options?: OptionType[]
  optionsFilterable?: boolean
  optionsValueKey?: any
  clearable?: boolean
  loading?: boolean
  disabled?: boolean
  lengthThreshold?: number
  style?: string
  mask?: string
  maskTokens?: string
}>()

const emit = defineEmits(['editSaved'])

const editInputRef = ref()
const editInputValue = ref<string | IModel>()
const editing = ref<boolean>(false)
const clickOutsideTarget = ref()
const selectVisible = ref<boolean>(false)

/**
 * Enter edit mode (hide value, show input or dropdown).
 */
const enterEditMode = async () => {
  if (props.disabled || props.loading) {
    return
  }

  editing.value = true
  editInputValue.value = props.val
  await nextTick()
  await editInputRef.value?.focus()
}

/**
 * Exit edit mode; emit new value or show error message on invalid input.
 */
const saveEdit = () => {
  exitEditMode()

  let newValue
  let changed

  if (typeof editInputValue.value === 'string') {
    newValue = editInputValue.value?.trim() || null
    changed = props.val !== newValue
  } else {
    newValue = editInputValue.value as IModel
    const initialValue = props.val as IModel
    changed = newValue?.valueKey !== initialValue?.valueKey
  }

  // Value didn't change, abort
  if (!changed) return

  // Show error on empty value and required
  if (props.required && !newValue) {
    ElMessage.error('A new value is required.')
    return
  }

  emit('editSaved', newValue ?? null)
}

/**
 * Exits edit mode.
 */
const exitEditMode = () => {
  editing.value = false
  selectVisible.value = false
}

/**
 * Edit focus loss handler.
 */
const handleFocusLoss = () => {
  // If select dropdown is visible, ignore outside click
  // I'd like to find a better way to do this, but I can't
  // assign a ref to the popper.js dropdown.
  if (!selectVisible.value) {
    saveEdit()
  }
}

/**
 * Gets display value for either string or IModel value.
 *
 * @param {string | IModel | undefined} value value to format.
 * @returns if null or string, the initial value. If model, a string with model and model version.
 */
const getDisplayValue = (value?: string | IModel) => {
  if (value === undefined || typeof value === 'string') return value
  return value.version ? `${value.name} ${value.version}` : value.name
}

/**
 * Save edit on outside click (ignore cancel button).
 */
onClickOutside(clickOutsideTarget, handleFocusLoss)

/**
 * Actual text to render, based on loading state besides display value.
 */
const textRendered = computed(() =>
  props.loading ? getDisplayValue(editInputValue.value) || 'Removing...' : (getDisplayValue(props.val) ?? '-')
)
</script>

<template>
  <div class="rl-editable-detail">
    <div v-if="editing" ref="clickOutsideTarget" class="flex flex-row gap-1">
      <div v-if="options" class="flex-1">
        <el-select
          ref="editInputRef"
          v-model="editInputValue"
          class="rl-editable-detail__edit--select"
          :clearable="clearable"
          :filterable="props.optionsFilterable"
          :value-key="props.optionsValueKey"
          @visible-change="(visible: boolean) => (selectVisible = visible)"
        >
          <el-option
            v-for="option in options"
            ref="optionRefs"
            :key="option.value.valueKey ?? option.label"
            :label="option.label"
            :value="option.value"
          />
        </el-select>
      </div>
      <el-input
        v-else
        ref="editInputRef"
        v-model="editInputValue as string"
        v-maska
        :style="style ?? undefined"
        :clearable="clearable"
        :data-maska="mask"
        :data-maska-tokens="maskTokens"
        @keyup.enter="saveEdit"
      />
      <el-button type="info" title="Cancel edit" link @click="exitEditMode"
        ><Icon icon="material-symbols:close-rounded" height="16"
      /></el-button>
    </div>
    <p
      v-else
      class="rl-editable-detail__view"
      :class="(loading && 'rl-editable-detail__view--loading') || (disabled && 'cursor-not-allowed')"
      @dblclick="enterEditMode"
    >
      <rc-display-serial-number v-if="lengthThreshold" :sn="textRendered" :threshold="lengthThreshold" />
      <span v-else>{{ textRendered }}</span>
    </p>
  </div>
</template>

<style lang="scss">
.rl-editable-detail {
  &__view {
    cursor: pointer;
    align-content: center;
    height: max-content;

    &--loading {
      cursor: wait;
      font-style: italic;
      color: var(--text-secondary);
    }
  }
}
</style>
